import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Wine, Plus, Minus, ShoppingCart, Heart, Star, Send } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [inWishlist, setInWishlist] = useState(false);
  const { addItem, itemCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${id}`);
        setProduct(res.data);
        // Track recently viewed items in localStorage
        try {
          const viewed = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
          const updatedViewed = [res.data, ...viewed.filter(p => p.id !== res.data.id)].slice(0, 10);
          localStorage.setItem('recently_viewed', JSON.stringify(updatedViewed));
        } catch {}
        // Auto-select first variant if available
        if (res.data.variants?.length > 0) {
          setSelectedVariant(res.data.variants[0]);
        }
        const relRes = await api.get('/products', { params: { category: res.data.category, limit: 4 } });
        setRelated(relRes.data.products.filter(p => p.id !== id).slice(0, 4));
        // Fetch reviews
        const revRes = await api.get(`/products/${id}/reviews`);
        setReviews(revRes.data.reviews);
        setAvgRating(revRes.data.avg_rating);
        // Check wishlist
        if (localStorage.getItem('auth_token')) {
          try {
            const wRes = await api.get('/wishlist');
            setInWishlist(wRes.data.items.some(p => p.id === id));
          } catch {}
        }
      } catch {
        toast.error('Product not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = () => {
    if (!product || product.stock <= 0) return;
    addItem(product, quantity, selectedVariant);
    toast.success(`${quantity}x ${product.name}${selectedVariant ? ` (${selectedVariant.name})` : ''} added to cart`);
  };

  const toggleWishlist = async () => {
    if (!user) return toast.error('Please login to save items');
    try {
      if (inWishlist) {
        await api.delete(`/wishlist/${id}`);
        setInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/wishlist/${id}`);
        setInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitReview = async () => {
    if (!user) return toast.error('Please login to leave a review');
    if (!reviewText.trim()) return toast.error('Please write a comment');
    try {
      const res = await api.post(`/products/${id}/reviews`, { rating: reviewRating, comment: reviewText });
      setReviews(prev => [res.data, ...prev]);
      setReviewText('');
      setReviewRating(5);
      toast.success('Review submitted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#cc3737] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!product) return null;

  const price = selectedVariant ? selectedVariant.price : (product.sale_price || product.price);

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="product-detail-page">
      {/* Header */}
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-16">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <Link to="/checkout" className="relative" data-testid="product-cart-icon">
            <ShoppingBag className="w-5 h-5 text-[#94A3B8]" />
            {itemCount > 0 && <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#cc3737] text-[10px] text-white flex items-center justify-center">{itemCount}</span>}
          </Link>
        </div>
      </nav>

      <div className="pt-24 pb-12 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="aspect-square bg-[#12151C] border border-white/5 rounded-xl overflow-hidden">
            <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover" />
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-4">
              {product.age_restricted && <span className="age-badge">21+</span>}
              {product.sale_price && <span className="sale-badge">ON SALE</span>}
              <span className="text-[#64748B] text-sm font-light">{product.category}</span>
            </div>
            <p className="text-[#b56b38] text-sm font-medium tracking-wider uppercase mb-2">{product.brand}</p>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-4" data-testid="product-name">{product.name}</h1>
            <p className="text-[#94A3B8] font-light leading-relaxed mb-6">{product.description}</p>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {product.abv && <div className="bg-[#12151C] border border-white/5 rounded-lg p-3"><span className="text-[#64748B] text-xs block">ABV</span><span className="text-white font-medium">{product.abv}%</span></div>}
              {product.proof && <div className="bg-[#12151C] border border-white/5 rounded-lg p-3"><span className="text-[#64748B] text-xs block">Proof</span><span className="text-white font-medium">{product.proof}</span></div>}
              {product.sizes?.length > 0 && <div className="bg-[#12151C] border border-white/5 rounded-lg p-3"><span className="text-[#64748B] text-xs block">Sizes</span><span className="text-white font-medium text-sm">{product.sizes.join(', ')}</span></div>}
              <div className="bg-[#12151C] border border-white/5 rounded-lg p-3"><span className="text-[#64748B] text-xs block">Stock</span><span className={`font-medium ${product.stock > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{product.stock > 0 ? `${product.stock} available` : 'Out of stock'}</span></div>
            </div>

            {/* Variant/Sub-item Selector */}
            {product.variants?.length > 0 && (
              <div className="mb-6" data-testid="variant-selector">
                <p className="text-[#94A3B8] text-sm mb-3">Select Option:</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedVariant(v)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${selectedVariant?.name === v.name ? 'bg-[#cc3737]/20 border-[#cc3737] text-white' : 'bg-[#12151C] border-white/10 text-[#94A3B8] hover:border-white/20'}`}
                      data-testid={`variant-btn-${i}`}
                    >
                      <span>{v.name}</span>
                      <span className="ml-2 text-[#b56b38] font-semibold">${v.price.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Price & Add to Cart */}
            <div className="flex items-end gap-3 mb-6">
              <span className="font-['Playfair_Display'] text-3xl font-medium text-white" data-testid="product-price">${price.toFixed(2)}</span>
              {product.sale_price && <span className="text-[#64748B] text-lg line-through mb-1">${product.price.toFixed(2)}</span>}
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center bg-[#12151C] border border-white/10 rounded-full">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 text-[#94A3B8] hover:text-white transition-colors" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center text-white font-medium" data-testid="qty-display">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-3 text-[#94A3B8] hover:text-white transition-colors" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
                className="flex-1 bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full py-3 font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-4 h-4" /> Add to Cart — ${(price * quantity).toFixed(2)}
              </button>
              <button onClick={toggleWishlist} className={`p-3 rounded-full border transition-all ${inWishlist ? 'bg-[#cc3737]/20 border-[#cc3737]/50 text-[#cc3737]' : 'border-white/10 text-[#64748B] hover:text-white hover:border-white/20'}`} data-testid="wishlist-toggle">
                <Heart className={`w-5 h-5 ${inWishlist ? 'fill-current' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-16" data-testid="reviews-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Playfair_Display'] text-2xl font-medium text-white">Reviews {avgRating > 0 && <span className="text-[#b56b38] text-lg ml-2">({avgRating} / 5)</span>}</h2>
          </div>

          {/* Add Review */}
          {user && (
            <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 mb-6" data-testid="add-review-form">
              <h3 className="text-white font-medium mb-3">Leave a Review</h3>
              <div className="flex gap-1 mb-3">
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setReviewRating(s)} className="p-0.5" data-testid={`rating-star-${s}`}>
                    <Star className={`w-5 h-5 ${s <= reviewRating ? 'text-[#b56b38] fill-[#b56b38]' : 'text-[#64748B]'}`} />
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Write your review..." className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38]" data-testid="review-text-input" />
                <button onClick={submitReview} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="submit-review-btn"><Send className="w-3.5 h-3.5" /> Post</button>
              </div>
            </div>
          )}

          {/* Review List */}
          {reviews.length === 0 ? (
            <p className="text-[#64748B] text-center py-8 bg-[#12151C] border border-white/5 rounded-xl">No reviews yet. Be the first!</p>
          ) : (
            <div className="space-y-3">
              {reviews.map((rev, i) => (
                <div key={i} className="bg-[#12151C] border border-white/5 rounded-xl p-4" data-testid={`review-item-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium">{rev.user_name}</span>
                      <div className="flex gap-0.5">{Array.from({length: 5}).map((_, j) => <Star key={j} className={`w-3.5 h-3.5 ${j < rev.rating ? 'text-[#b56b38] fill-[#b56b38]' : 'text-[#64748B]'}`} />)}</div>
                    </div>
                    <span className="text-[#64748B] text-xs">{new Date(rev.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-[#94A3B8] text-sm">{rev.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-['Playfair_Display'] text-2xl font-medium text-white mb-6">You Might Also Like</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {related.map((p, i) => (
                <Link to={`/shop/product/${p.id}`} key={p.id} className="product-card bg-[#12151C] border border-white/5 rounded-xl overflow-hidden" data-testid={`related-product-${i}`}>
                  <div className="aspect-square bg-[#1A1D27]"><img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>
                  <div className="p-3">
                    <p className="text-[#64748B] text-xs">{p.brand}</p>
                    <h3 className="text-white text-sm font-medium line-clamp-1">{p.name}</h3>
                    <span className="text-[#b56b38] font-semibold text-sm">${(p.sale_price || p.price).toFixed(2)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
